import moment from 'moment';
import type {Call} from '../../store/features/calls/callsService';
import type {RoomWithLastMessage} from '../../hooks/useMatrix';
import {isGroupCall, isMissedIncomingCall} from '../../utilis/callsUtilis';
import {getFullName} from '../../utilis/getFullName';
import {getFormatedPhoneNumber} from '../../utilis/msisdnUtilis';

/** Missed incoming calls that started today, newest first. */
export function missedCallsToday(calls: Call[], now: moment.Moment = moment()): Call[] {
    return calls
        .filter((call) => isMissedIncomingCall(call) && moment(call.startedAt).isSame(now, 'day'))
        .sort((a, b) => moment(b.startedAt).valueOf() - moment(a.startedAt).valueOf());
}

export interface MissedCaller {
    readonly call: Call;
    readonly count: number;
}

const callerKey = (call: Call): string => {
    if (isGroupCall(call) && call.roomId) return `room:${call.roomId}`;
    if (call.internalCallee?.userId != null) return `user:${call.internalCallee.userId}`;
    if (call.primaryContact?.contactId != null) return `contact:${call.primaryContact.contactId}`;
    return call.clientMsisdn ? `number:${call.clientMsisdn}` : `call:${call.callId}`;
};

export function missedCallersToday(calls: Call[], now: moment.Moment = moment()): MissedCaller[] {
    const callers = new Map<string, MissedCaller>();
    for (const call of missedCallsToday(calls, now)) {
        const key = callerKey(call);
        const seen = callers.get(key);
        callers.set(key, seen ? {...seen, count: seen.count + 1} : {call, count: 1});
    }
    return [...callers.values()];
}

/** Contact or colleague name, or the formatted number when the caller is unknown. */
export function callerName(call: Call, roomName?: (roomId: string) => string | undefined): string {
    if (isGroupCall(call)) return (call.roomId && roomName?.(call.roomId)) || 'Group call';
    if (call.internalCallee) return getFullName(call.internalCallee.firstName, call.internalCallee.lastName);
    if (call.primaryContact) return getFullName(call.primaryContact.firstName, call.primaryContact.lastName);
    return call.clientMsisdn ? getFormatedPhoneNumber(call.clientMsisdn, false) ?? '' : 'Unknown';
}

export function isKnownCaller(call: Call): boolean {
    return Boolean(call.internalCallee || call.primaryContact);
}

/** Rooms with unread messages, most recent first. */
export function unreadRooms(rooms: RoomWithLastMessage[]): RoomWithLastMessage[] {
    return rooms
        .filter((room) => room.unreadCount > 0)
        .sort((a, b) => (b.lastMessage?.timestamp ?? 0) - (a.lastMessage?.timestamp ?? 0));
}

export function roomName(room: RoomWithLastMessage): string {
    return room.room?.name || room.roomName || room.otherUsers?.map((u) => u.displayName).filter(Boolean).join(', ') || 'Chat';
}
